import { z } from 'zod';
import { apiEnvelope } from './v1.js';

/**
 * Per-idea cost ledger (IDEA-14, 2026-07-06 revision).
 *
 * The GATEWAY writes one append-only cost event per spend-incurring call — it is the only
 * tier that sees real token usage and owns the price table (the browser must never see
 * pricing). `kind` is a generic discriminator: `'llm'` today; future non-LLM costs
 * (connector fees, data purchases, compute) ride the same row with the LLM-specific
 * fields absent. `ideaId` is optional because some spend predates the idea it produces
 * (ideate/refine) — those rows are portfolio-level.
 */
export const CostEventSchema = z.object({
  id: z.string().min(1).max(64),
  /** Absent for pre-idea spend (ideate/refine) — portfolio-level rows. */
  ideaId: z.string().min(1).max(64).optional(),
  /** Cost family: 'llm' today; generic by design (connector, data, compute, …). */
  kind: z.string().min(1).max(40),
  /** What the spend was for, e.g. 'generate:tam', 'spec', 'explain:qsam', 'ideate'. */
  op: z.string().min(1).max(120),
  /** LLM-only: the model that actually served the call (tier routing may differ from the request). */
  model: z.string().min(1).max(120).optional(),
  inputTokens: z.number().int().nonnegative().optional(),
  outputTokens: z.number().int().nonnegative().optional(),
  /** LLM-only: the $/1M-token rates applied (pinned server-side price table). */
  inPerMtok: z.number().nonnegative().optional(),
  outPerMtok: z.number().nonnegative().optional(),
  /** USD cost of this event (micro-dollar precision). */
  costUsd: z.number().nonnegative(),
  /** Wall-clock of the brokered call in ms (IDEA-48) — "how long did this cost to wait for". */
  durationMs: z.number().int().nonnegative().optional(),
  correlationId: z.string().min(1).max(120).optional(),
  /** Free-text context for non-LLM kinds (vendor, unit count, …). */
  detail: z.string().max(500).optional(),
  /** Gateway clock, epoch ms. */
  createdAt: z.number().int().nonnegative(),
});
export type CostEvent = z.infer<typeof CostEventSchema>;

/** `POST /v1/costs/list` — the ledger for one idea (owner-scoped, newest first, bounded). */
export const ListCostsRequestSchema = z.object({
  ownerId: z.string().min(1).max(120),
  ideaId: z.string().min(1).max(64),
});
export type ListCostsRequest = z.infer<typeof ListCostsRequestSchema>;

export const IdeaCostsSchema = z.object({
  events: z.array(CostEventSchema),
  /** Sum of every event's costUsd for this idea (ALL rows, not just the returned page). */
  totalUsd: z.number().nonnegative(),
});
export type IdeaCosts = z.infer<typeof IdeaCostsSchema>;

export const ListCostsResponseSchema = apiEnvelope(IdeaCostsSchema);
export type ListCostsResponse = z.infer<typeof ListCostsResponseSchema>;

/**
 * `POST /v1/costs/by-correlation` — every spend row one action produced (IDEA-63 / A1).
 * The correlation id is the join key that threads a user action → its events → its run →
 * its cost rows, so this read answers "what did THAT action cost" (and, for Jarvis,
 * "what did this turn cost") across ideas — including idea-less portfolio rows the
 * per-idea read can never see. Owner rides the request like the per-idea read; rows carry
 * no owner until the identity hardening (DEVOP-4) — single-tenant today, gated then.
 */
export const ListCostsByCorrelationRequestSchema = z.object({
  ownerId: z.string().min(1).max(120),
  correlationId: z.string().min(1).max(120),
});
export type ListCostsByCorrelationRequest = z.infer<typeof ListCostsByCorrelationRequestSchema>;

export const CorrelationCostsSchema = z.object({
  events: z.array(CostEventSchema),
  /** Sum of every event's costUsd for this correlation id (ALL rows, not just the page). */
  totalUsd: z.number().nonnegative(),
});
export type CorrelationCosts = z.infer<typeof CorrelationCostsSchema>;

export const ListCostsByCorrelationResponseSchema = apiEnvelope(CorrelationCostsSchema);
export type ListCostsByCorrelationResponse = z.infer<typeof ListCostsByCorrelationResponseSchema>;
